import React from 'react';
import { Typography } from '../../../interfaces/typography';
import { CustomDefinition, CustomMetric, CustomMetricSource, CustomMetricValueType } from "../../../interfaces/db";
import { CustomDetail } from "../../../interfaces/db";
import { getCustomCode, OutputType } from "../../CustomScores/cfg";
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Check, X, ChevronUp, ChevronDown } from 'lucide-react';

// Professional color palette
const COLORS = {
    success: {
        main: '#2E7D32',
        bg: 'rgba(46, 125, 50, 0.08)',
        border: 'rgba(46, 125, 50, 0.15)'
    },
    error: {
        main: '#D32F2F',
        bg: 'rgba(211, 47, 47, 0.08)',
        border: 'rgba(211, 47, 47, 0.15)'
    },
    warning: {
        main: '#ED6C02',
        bg: 'rgba(237, 108, 2, 0.08)',
        border: 'rgba(237, 108, 2, 0.15)'
    },
    neutral: {
        main: '#757575',
        bg: 'rgba(117, 117, 117, 0.08)',
        border: 'rgba(117, 117, 117, 0.15)'
    }
};

interface ScoreMetricProps {
    customDetail: CustomDetail | null
    customMetric: CustomMetric | null
    teamId?: string
}

export const ScoreMetric: React.FC<ScoreMetricProps> = ({ 
    customDetail, 
    customMetric, 
    teamId 
}) => {
    const customDefinitions = useSelector((state: RootState) => state.customDefinitions.value) ?? [];

    const getMetricValue = (def: CustomDefinition): { value: string | number | boolean | null, explanation: string | null } => {
        if (!customMetric || !customDetail || def.metric_idx === null) {
            return { value: null, explanation: null };
        }
        
        const metricKey = `metric_${def.metric_idx}` as keyof CustomMetric;
        const value = customMetric[metricKey];

        let explanation: string | null = null;
        try {
            if (customDetail.metric_to_explanation_json_str) {
                const explanations = JSON.parse(customDetail.metric_to_explanation_json_str) as Record<string, string>;
                explanation = explanations[def.metric_idx.toString()] || null;
            }
        } catch (e) {
            console.error('Failed to parse explanation:', e);
        }

        if (value instanceof Date || typeof value === 'object') {
            return { value: null, explanation };
        }

        return { value: value as string | number | boolean | null, explanation };
    };

    const renderMetricValue = (def: CustomDefinition, value: any, explanation: string | null) => {
        let displayValue: React.ReactNode;
        let colors = COLORS.neutral;
        let icon: React.ReactNode = null;

        // If value is null, show dash with neutral colors
        if (value === null) {
            displayValue = '-';
            return renderMetricBox(def, displayValue, COLORS.neutral, explanation, icon);
        }

        // Handle custom code metrics
        if (def.value_source === CustomMetricSource.CODE && def.value_code_str && teamId) {
            const customCodes = getCustomCode(teamId);
            const code = customCodes?.find(c => c.name === def.value_code_str);
            
            if (code) {
                switch (code.output_type) {
                    case OutputType.BOOLEAN:
                        colors = Boolean(value) ? COLORS.success : COLORS.error;
                        displayValue = Boolean(value) ? 'Yes' : 'No';
                        icon = Boolean(value) ? <Check size={12} /> : <X size={12} />;
                        break;
                    case OutputType.PERCENTAGE:
                        const percentage = value !== null ? Math.round(Number(value) * 100) : 0;
                        if (percentage >= 70) {
                            colors = COLORS.success;
                            icon = <ChevronUp size={12} />;
                        } else if (percentage >= 30) {
                            colors = COLORS.warning;
                        } else {
                            colors = COLORS.error;
                            icon = <ChevronDown size={12} />;
                        }
                        displayValue = `${percentage}%`;
                        break;
                    case OutputType.ENUM:
                        if (!code.enum_options || value === null) {
                            displayValue = '-';
                            break;
                        }
                        displayValue = code.enum_options[Number(value)] || '-';
                        break;
                }
                
                return renderMetricBox(def, displayValue, colors, explanation, icon);
            }
        }

        // Handle standard metrics
        switch (def.value_type) {
            case CustomMetricValueType.BOOLEAN:
                colors = Boolean(value) ? COLORS.success : COLORS.error;
                displayValue = Boolean(value) ? 'Yes' : 'No';
                icon = Boolean(value) ? <Check size={12} /> : <X size={12} />;
                break;
            case CustomMetricValueType.PERCENT:
                const percentage = value !== null ? Math.round(Number(value) * 100) : 0;
                if (percentage >= 70) {
                    colors = COLORS.success;
                    icon = <ChevronUp size={12} />;
                } else if (percentage >= 30) {
                    colors = COLORS.warning;
                } else {
                    colors = COLORS.error;
                    icon = <ChevronDown size={12} />;
                }
                displayValue = `${percentage}%`;
                break;
            case CustomMetricValueType.ENUM:
                if (!def.value_type_enum_str || value === null) {
                    displayValue = '-';
                    break;
                }
                const categories = def.value_type_enum_str.split(',');
                displayValue = categories[Number(value)] || '-';
                break;
            default:
                displayValue = String(value || '-');
        }

        return renderMetricBox(def, displayValue, colors, explanation, icon);
    };

    const renderMetricBox = (def: CustomDefinition, displayValue: React.ReactNode, colors: typeof COLORS[keyof typeof COLORS], explanation: string | null, icon: React.ReactNode) => {
        const hasTooltip = def.description || explanation;

        return (
            <div 
                key={def.metric_idx}
                className="relative group inline-flex items-center h-6 px-2 rounded-md"
                style={{ 
                    backgroundColor: colors.bg, 
                    border: `1px solid ${colors.border}`,
                    cursor: hasTooltip ? 'help' : 'default',
                    transition: 'all 0.15s ease'
                }}
            >
                <div className="flex items-center gap-1.5">
                    <Typography 
                        variant="smallParagraph"
                        color="#555"
                        className="font-medium whitespace-nowrap"
                    >
                        {def.label}
                    </Typography>
                    <Typography 
                        variant="smallParagraph"
                        fontWeight="600"
                        color={colors.main}
                        className="flex items-center gap-0.5 whitespace-nowrap"
                    >
                        {icon && <span className="flex items-center">{icon}</span>}
                        {displayValue}
                    </Typography>
                </div>
                
                {/* Tooltip with fixed positioning to prevent cutoff */}
                {hasTooltip && (
                    <div 
                        className="absolute left-1/2 -translate-x-1/2 mt-2 p-2 bg-white text-black rounded shadow-lg 
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-[99] 
                                  border border-gray-200 w-max max-w-xs pointer-events-none"
                        style={{ 
                            minWidth: '150px',
                            top: '100%',
                            filter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))'
                        }}
                    >
                        <div className="flex flex-col gap-1.5">
                            {def.description && (
                                <Typography variant="smallParagraph" fontWeight="600">
                                    {def.description}
                                </Typography>
                            )}
                            {explanation && (
                                <Typography variant="smallParagraph" color="#666">
                                    {explanation}
                                </Typography>
                            )}
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-2 h-2 bg-white rotate-45 border-l border-t border-gray-200 z-[99]"></div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-wrap items-center gap-2 max-w-full overflow-visible">
            {customDefinitions.map((def) => {
                const { value, explanation } = getMetricValue(def);
                return renderMetricValue(def, value, explanation);
            })}
        </div>
    );
}; 